import { useState } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters, startOfYear, endOfYear, subYears } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown, Lock, RotateCcw } from 'lucide-react';
import { TimeGranularity, DatePreset } from '@/types/reports';
import type { PreviousDateSettings } from '@/types/reports';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { DateRange } from 'react-day-picker';

interface TimePeriodSelectorProps {
  granularity: TimeGranularity;
  periodCount: number;
  useFullGroup: boolean;
  onGranularityChange: (granularity: TimeGranularity) => void;
  onPeriodCountChange: (count: number) => void;
  onUseFullProductionChange: (useFullGroup: boolean) => void;
  // Date range props
  datePreset?: DatePreset;
  customStartDate?: Date | null;
  customEndDate?: Date | null;
  onDatePresetChange?: (preset: DatePreset) => void;
  onCustomDateRangeChange?: (start: Date | null, end: Date | null) => void;
  disabled?: boolean;
  /** Dynamic label for group (Production vs Group based on industry) */
  groupLabel?: string;
  /** Saved date settings to restore when exiting "Use Production Dates" mode */
  previousDateSettings?: PreviousDateSettings;
}

const GRANULARITY_OPTIONS: { value: TimeGranularity; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// Quick preset buttons for common ranges
const QUICK_PRESETS: { label: string; value: DatePreset }[] = [
  { label: '7D', value: 'last-7-days' },
  { label: '30D', value: 'last-30-days' },
  { label: '90D', value: 'last-90-days' },
  { label: 'MTD', value: 'this-month' },
  { label: 'QTD', value: 'this-quarter' },
  { label: 'YTD', value: 'this-year' },
];

function getDateRangeFromPreset(preset: DatePreset): { from: Date; to: Date } | null {
  const now = new Date();

  switch (preset) {
    case 'last-7-days':
      return { from: subDays(now, 7), to: now };
    case 'last-30-days':
      return { from: subDays(now, 30), to: now };
    case 'last-90-days':
      return { from: subDays(now, 90), to: now };
    case 'this-month':
      return { from: startOfMonth(now), to: now };
    case 'last-month': {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    case 'this-quarter':
      return { from: startOfQuarter(now), to: now };
    case 'last-quarter': {
      const lastQuarter = subQuarters(now, 1);
      return { from: startOfQuarter(lastQuarter), to: endOfQuarter(lastQuarter) };
    }
    case 'this-year':
      return { from: startOfYear(now), to: now };
    case 'last-year': {
      const lastYear = subYears(now, 1);
      return { from: startOfYear(lastYear), to: endOfYear(lastYear) };
    }
    default:
      return null;
  }
}

export function TimePeriodSelector({
  granularity,
  periodCount: _periodCount,
  useFullGroup,
  onGranularityChange,
  onPeriodCountChange: _onPeriodCountChange,
  onUseFullProductionChange,
  datePreset = 'last-30-days',
  customStartDate,
  customEndDate,
  onDatePresetChange,
  onCustomDateRangeChange,
  disabled = false,
  groupLabel = 'Production',
  previousDateSettings: _previousDateSettings,
}: TimePeriodSelectorProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Tailwind's sm breakpoint, not useIsMobile's 768px: between 640 and 768
  // the sm: styles are already active, so the calendar must match them.
  const isNarrow = useMediaQuery('(max-width: 639.98px)');

  // Get current date range for display
  const getDateRangeDisplay = () => {
    if (datePreset === 'custom' && customStartDate && customEndDate) {
      return `${format(customStartDate, 'MMM d')} - ${format(customEndDate, 'MMM d, yyyy')}`;
    }
    if (datePreset === 'custom') {
      return 'Choose date range';
    }
    const range = getDateRangeFromPreset(datePreset);
    if (range) {
      return `${format(range.from, 'MMM d')} - ${format(range.to, 'MMM d, yyyy')}`;
    }
    return 'Select range';
  };

  // Get the current range for calendar display
  const getCurrentRange = (): DateRange | undefined => {
    if (datePreset === 'custom' && customStartDate && customEndDate) {
      return { from: customStartDate, to: customEndDate };
    }
    if (datePreset !== 'custom') {
      const range = getDateRangeFromPreset(datePreset);
      if (range) return range;
    }
    return undefined;
  };

  const handleQuickPreset = (preset: DatePreset) => {
    // Only call onDatePresetChange - it now handles clearing custom date range too
    // This avoids the race condition where two separate calls with stale props overwrote each other
    onDatePresetChange?.(preset);
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    // Only call onCustomDateRangeChange - it now handles setting preset to 'custom' too
    // This avoids the race condition where two separate calls with stale props overwrote each other
    if (range?.from && range?.to) {
      onCustomDateRangeChange?.(range.from, range.to);
    } else if (range?.from) {
      onCustomDateRangeChange?.(range.from, null);
    }
  };

  return (
    <div className="space-y-3">
      {/* Quick Preset Buttons - contained in rounded background */}
      <div className="flex bg-muted/30 rounded-md p-0.5">
          {QUICK_PRESETS.map((preset) => (
            <Button
              key={preset.value}
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled || useFullGroup}
              onClick={() => handleQuickPreset(preset.value)}
              className={cn(
                "h-6 px-1 text-xs flex-1 rounded-sm min-w-0",
                datePreset === preset.value
                  ? "bg-[#8FB560] text-black hover:bg-[#8FB560]/90 shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-transparent"
              )}
            >
              {preset.label}
            </Button>
          ))}
        </div>

      {/* Locked Production Dates Banner OR Calendar Popover */}
      {useFullGroup ? (
        <Button
          variant="outline"
          className="w-full justify-between h-9 text-sm font-normal bg-muted/30 cursor-default hover:bg-muted/30"
          asChild
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="h-3 w-3 text-[#8FB560]" />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">
                Using {groupLabel.charAt(0).toUpperCase() + groupLabel.slice(1)} Dates
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                // Disable useFullGroup - the parent handler will restore previous settings atomically
                onUseFullProductionChange(false);
              }}
              className="flex items-center justify-center h-6 w-6 rounded-md bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors ml-2 shrink-0"
              aria-label="Clear group dates"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          </div>
        </Button>
      ) : (
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              disabled={disabled}
              className="w-full justify-between h-9 text-sm font-normal"
            >
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{getDateRangeDisplay()}</span>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              defaultMonth={customStartDate || undefined}
              selected={getCurrentRange()}
              onSelect={handleRangeSelect}
              // Two months side by side overflow a phone popover; one fits.
              numberOfMonths={isNarrow ? 1 : 2}
              disabled={(date) => date > new Date()}
            />
            <div className="border-t border-border p-3 flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Clear custom dates and set to custom mode with no selection
                  // This shows "Choose date range" in the button label
                  onCustomDateRangeChange?.(null, null);
                }}
              >
                Reset
              </Button>
              <Button
                size="sm"
                onClick={() => setCalendarOpen(false)}
                className="bg-[#8FB560] hover:bg-[#8FB560]/90"
              >
                Apply
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Group By Toggle - stays enabled even when useFullGroup is true */}
      <div className="flex bg-muted/30 rounded-md p-0.5">
        {GRANULARITY_OPTIONS.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onGranularityChange(option.value)}
            className={cn(
              "flex-1 rounded-sm text-xs h-6",
              granularity === option.value
                ? "bg-[#8FB560] text-black hover:bg-[#8FB560]/90 shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-transparent"
            )}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
